import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";

import {
  PortalGatewayError,
  TERMINAL_RUN_STATES,
  type PortalGateway,
} from "./api";
import {
  initialRunUiState,
  runUiReducer,
} from "./model";

const MAX_STREAM_ATTEMPTS = 4;
const RETRY_DELAYS_MILLISECONDS = [250, 500, 1_000, 2_000];
const REPLAY_STOP_CODES = new Set([
  "STREAM_EXPIRED",
  "STREAM_BACKLOG_EXCEEDED",
  "STREAM_CURSOR_INVALID",
]);

export const retryDelayMilliseconds = (
  attempt: number,
  randomValue = Math.random(),
): number => {
  const base =
    RETRY_DELAYS_MILLISECONDS[
      Math.min(
        Math.max(0, Math.trunc(attempt)),
        RETRY_DELAYS_MILLISECONDS.length - 1,
      )
    ];
  const boundedRandom = Math.min(1, Math.max(0, randomValue));
  return Math.round(base * (0.8 + boundedRandom * 0.4));
};

const abortableDelay = (
  milliseconds: number,
  signal: AbortSignal,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      window.clearTimeout(timeout);
      reject(signal.reason);
    };
    const timeout = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);
    signal.addEventListener("abort", onAbort, { once: true });
  });

const isAbort = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

export const useM1Run = (
  gateway: PortalGateway | null,
  runId: string | null,
) => {
  const [state, dispatch] = useReducer(
    runUiReducer,
    gateway
      ? {
          ...initialRunUiState,
          connection: runId ? "loading" : "no-run",
        }
      : initialRunUiState,
  );
  const streamController = useRef<AbortController | null>(null);

  useEffect(() => {
    streamController.current?.abort();
    if (!gateway) {
      dispatch({ type: "no_gateway" });
      return undefined;
    }
    if (!runId) {
      dispatch({ type: "no_run" });
      return undefined;
    }

    const controller = new AbortController();
    streamController.current = controller;
    dispatch({ type: "load_started" });

    const observe = async () => {
      let cursor = 0;
      try {
        let snapshot = await gateway.readRun(runId, controller.signal);
        dispatch({ type: "snapshot_loaded", snapshot });
        if (
          TERMINAL_RUN_STATES.has(snapshot.state) &&
          snapshot.last_sequence === 0
        ) {
          return;
        }

        for (let attempt = 0; attempt < MAX_STREAM_ATTEMPTS; attempt += 1) {
          try {
            for await (const event of gateway.streamRun(
              runId,
              cursor,
              controller.signal,
            )) {
              dispatch({ type: "stream_event", event });
              if (event.kind !== "stream-control") {
                cursor = Math.max(cursor, event.sequence);
                if (
                  event.kind === "run-state" &&
                  TERMINAL_RUN_STATES.has(event.state)
                ) {
                  return;
                }
              } else {
                snapshot = await gateway.readRun(
                  runId,
                  controller.signal,
                );
                dispatch({ type: "snapshot_loaded", snapshot });
                dispatch({
                  type: "stream_interrupted",
                  code: event.code,
                });
                return;
              }
            }
            snapshot = await gateway.readRun(runId, controller.signal);
            dispatch({ type: "snapshot_loaded", snapshot });
            if (TERMINAL_RUN_STATES.has(snapshot.state)) {
              return;
            }
            throw new PortalGatewayError({
              code: "STREAM_DISCONNECTED",
              retryable: true,
            });
          } catch (error) {
            if (isAbort(error)) {
              return;
            }
            if (
              error instanceof PortalGatewayError &&
              REPLAY_STOP_CODES.has(error.code)
            ) {
              snapshot = await gateway.readRun(
                runId,
                controller.signal,
              );
              dispatch({ type: "snapshot_loaded", snapshot });
              dispatch({
                type: "stream_interrupted",
                code: error.code,
              });
              return;
            }
            if (
              !(error instanceof PortalGatewayError) ||
              !error.retryable ||
              attempt === MAX_STREAM_ATTEMPTS - 1
            ) {
              throw error;
            }
            dispatch({ type: "reconnecting" });
            await abortableDelay(
              retryDelayMilliseconds(attempt),
              controller.signal,
            );
          }
        }
      } catch (error) {
        if (isAbort(error)) {
          return;
        }
        const failure =
          error instanceof PortalGatewayError
            ? error
            : new PortalGatewayError({ code: "PORTAL_REQUEST_FAILED" });
        dispatch({
          type: "failed",
          code: failure.code,
          unavailable: failure.bootstrapInvalid,
        });
      }
    };

    void observe();
    return () => {
      controller.abort();
      if (streamController.current === controller) {
        streamController.current = null;
      }
    };
  }, [gateway, runId]);

  const confirmRevoke = useCallback(
    () => dispatch({ type: "revoke_confirming" }),
    [],
  );
  const dismissRevoke = useCallback(
    () => dispatch({ type: "revoke_dismissed" }),
    [],
  );
  const revoke = useCallback(async () => {
    if (!gateway || !runId || state.revoke === "applying") {
      return;
    }
    const controller = new AbortController();
    dispatch({ type: "revoke_started" });
    try {
      const command = await gateway.revokeRun(runId, controller.signal);
      dispatch({ type: "revoke_completed", command });
      streamController.current?.abort();
    } catch (error) {
      if (isAbort(error)) {
        return;
      }
      const failure =
        error instanceof PortalGatewayError
          ? error
          : new PortalGatewayError({ code: "REVOCATION_FAILED" });
      dispatch({
        type: "revoke_failed",
        code: failure.code,
        unavailable: failure.bootstrapInvalid,
      });
    }
  }, [gateway, runId, state.revoke]);

  return {
    state,
    confirmRevoke,
    dismissRevoke,
    revoke,
  };
};
